import {
  ArrowLeft,
  CheckCircle,
  Circle,
  Clock,
  FileCheck,
  FileText,
  Hourglass,
  Loader2,
  Pencil,
  Search,
  Upload,
  Video,
  XCircle,
  type LucideIcon,
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';

import { cn } from '@/lib/utils';
import { useApplicationStore, type ExistingApplication } from '@/store/applicationStore';
import { useAuthStore } from '@/store/authStore';
import { useDocumentStore } from '@/store/documentStore';

type StepStatus = 'completed' | 'action_required' | 'in_progress' | 'pending';

interface TimelineStep {
  id: number;
  titleKey: string;
  descKey: string;
  status: StepStatus;
  icon: typeof FileText;
  actionKey?: string;
  onAction?: () => void;
  timestamp?: string;
}

type AppStatus =
  | 'draft'
  | 'submitted'
  | 'video_pending'
  | 'video_completed'
  | 'under_review'
  | 'approved'
  | 'rejected'
  | 'waitlist';

interface StepStatuses {
  application: StepStatus;
  documents: StepStatus;
  interview: StepStatus;
  review: StepStatus;
}

const DOC_RELATED_REASON_CODES = new Set(['incomplete_docs', 'license_expired', 'pending_license_renewal']);

function deriveStepStatuses(
  status: string,
  documentsComplete: boolean,
  decisionContext?: { reasonCode?: string },
): StepStatuses {
  const s = status as AppStatus;
  const docStatus: StepStatus = documentsComplete ? 'completed' : 'action_required';

  switch (s) {
    case 'draft':
      return { application: 'in_progress', documents: 'pending', interview: 'pending', review: 'pending' };
    case 'submitted':
      return { application: 'completed', documents: docStatus, interview: 'pending', review: 'pending' };
    case 'video_pending':
      return { application: 'completed', documents: docStatus, interview: 'action_required', review: 'pending' };
    case 'video_completed':
      return { application: 'completed', documents: docStatus, interview: 'completed', review: 'pending' };
    case 'under_review':
      return { application: 'completed', documents: docStatus, interview: 'completed', review: 'in_progress' };
    case 'waitlist': {
      const docs: StepStatus =
        decisionContext?.reasonCode && DOC_RELATED_REASON_CODES.has(decisionContext.reasonCode)
          ? 'action_required'
          : 'completed';
      return { application: 'completed', documents: docs, interview: 'completed', review: 'completed' };
    }
    case 'approved':
    case 'rejected':
      return { application: 'completed', documents: 'completed', interview: 'completed', review: 'completed' };
    default:
      return { application: 'pending', documents: 'pending', interview: 'pending', review: 'pending' };
  }
}

function getStatusHeaderKey(status: string): string {
  switch (status as AppStatus) {
    case 'submitted':
      return 'dashboard.statusHeader.submitted';
    case 'video_pending':
      return 'dashboard.statusHeader.videoPending';
    case 'video_completed':
      return 'dashboard.statusHeader.videoCompleted';
    case 'under_review':
      return 'dashboard.statusHeader.underReview';
    case 'approved':
      return 'dashboard.statusHeader.approved';
    case 'rejected':
      return 'dashboard.statusHeader.rejected';
    case 'waitlist':
      return 'dashboard.statusHeader.waitlist';
    default:
      return 'dashboard.statusHeader.submitted';
  }
}

const TOTAL_WIZARD_STEPS = 6;

function formatRelativeUpdated(updatedAt: string, t: (key: string, opts?: Record<string, unknown>) => string): string {
  const then = new Date(updatedAt).getTime();
  if (Number.isNaN(then)) return '';
  const diffMs = Date.now() - then;
  const minutes = Math.floor(diffMs / 60000);
  if (minutes < 1) return t('dashboard.updatedRelative.justNow');
  if (minutes < 60) return t('dashboard.updatedRelative.minutes', { count: minutes });
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return t('dashboard.updatedRelative.hours', { count: hours });
  const days = Math.floor(hours / 24);
  if (days <= 14) return t('dashboard.updatedRelative.days', { count: days });
  return t('dashboard.updatedRelative.date', { date: new Date(updatedAt).toLocaleDateString() });
}

function getStatusMessageKey(status: string): string {
  switch (status as AppStatus) {
    case 'submitted':
      return 'dashboard.statusMessage.submitted';
    case 'video_pending':
      return 'dashboard.statusMessage.videoPending';
    case 'video_completed':
      return 'dashboard.statusMessage.videoCompleted';
    case 'under_review':
      return 'dashboard.statusMessage.underReview';
    case 'approved':
      return 'dashboard.statusMessage.approved';
    case 'rejected':
      return 'dashboard.statusMessage.rejected';
    case 'waitlist':
      return 'dashboard.statusMessage.waitlist';
    default:
      return 'dashboard.statusMessage.submitted';
  }
}

function getStatusIconStyle(status: string): { bg: string; text: string } {
  switch (status as AppStatus) {
    case 'approved':
      return { bg: 'bg-green-100', text: 'text-green-600' };
    case 'rejected':
      return { bg: 'bg-red-100', text: 'text-red-600' };
    case 'waitlist':
      return { bg: 'bg-amber-100', text: 'text-amber-600' };
    case 'under_review':
      return { bg: 'bg-blue-100', text: 'text-blue-600' };
    default:
      return { bg: 'bg-green-100', text: 'text-green-600' };
  }
}

function getMessageBannerStyle(status: string): string {
  switch (status as AppStatus) {
    case 'approved':
      return 'bg-green-50 text-green-800';
    case 'rejected':
      return 'bg-red-50 text-red-800';
    case 'waitlist':
      return 'bg-amber-50 text-amber-800';
    default:
      return 'bg-blue-50 text-blue-800';
  }
}

function getStatusBadgeStyle(status: string): string {
  switch (status as AppStatus) {
    case 'draft':
      return 'bg-gray-100 text-gray-700';
    case 'submitted':
      return 'bg-blue-100 text-blue-700';
    case 'video_pending':
    case 'video_completed':
      return 'bg-purple-100 text-purple-700';
    case 'under_review':
      return 'bg-amber-100 text-amber-700';
    case 'approved':
      return 'bg-green-100 text-green-700';
    case 'rejected':
      return 'bg-red-100 text-red-700';
    case 'waitlist':
      return 'bg-amber-100 text-amber-700';
    default:
      return 'bg-gray-100 text-gray-700';
  }
}

interface NextStepVisual {
  icon: LucideIcon;
  text: string;
}

function getNextStepVisual(status: string): NextStepVisual {
  switch (status as AppStatus) {
    case 'draft':
      return { icon: Pencil, text: 'text-gray-700' };
    case 'submitted':
      return { icon: Clock, text: 'text-blue-600' };
    case 'video_pending':
      return { icon: Video, text: 'text-purple-600' };
    case 'video_completed':
      return { icon: CheckCircle, text: 'text-purple-600' };
    case 'under_review':
      return { icon: Search, text: 'text-amber-600' };
    case 'approved':
      return { icon: CheckCircle, text: 'text-green-600' };
    case 'rejected':
      return { icon: XCircle, text: 'text-red-600' };
    case 'waitlist':
      return { icon: Hourglass, text: 'text-amber-600' };
    default:
      return { icon: FileCheck, text: 'text-gray-600' };
  }
}

function getStatusDotColor(status: string): string {
  switch (status as AppStatus) {
    case 'draft':
      return 'bg-gray-400';
    case 'submitted':
      return 'bg-blue-500';
    case 'video_pending':
    case 'video_completed':
      return 'bg-purple-500';
    case 'under_review':
      return 'bg-amber-500';
    case 'approved':
      return 'bg-green-500';
    case 'rejected':
      return 'bg-red-500';
    case 'waitlist':
      return 'bg-amber-500';
    default:
      return 'bg-gray-400';
  }
}

export function DashboardPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const checkExistingApplications = useApplicationStore((state) => state.checkExistingApplications);
  const applicant = useAuthStore((state) => state.applicant);
  const loadChecklist = useDocumentStore((state) => state.loadChecklist);
  const getDocumentProgress = useDocumentStore((state) => state.getProgress);

  const [applications, setApplications] = useState<ExistingApplication[]>([]);
  const [selectedApp, setSelectedApp] = useState<ExistingApplication | null>(null);
  const [documentsComplete, setDocumentsComplete] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    checkExistingApplications()
      .then((apps) => {
        setApplications(apps);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [checkExistingApplications]);

  useEffect(() => {
    const applicationId = searchParams.get('applicationId');
    if (!applicationId) return;

    const appFromUrl = applications.find((app) => app.applicationId === applicationId);
    if (appFromUrl) {
      setSelectedApp(appFromUrl);
    }
  }, [applications, searchParams]);

  useEffect(() => {
    if (!selectedApp || selectedApp.status === 'draft') {
      return undefined;
    }
    const applicationId = selectedApp.applicationId;
    let isCurrent = true;
    const fetchDocumentProgress = async () => {
      await loadChecklist(applicationId);
      if (!isCurrent) return;
      if (useDocumentStore.getState().applicationId !== applicationId) {
        setDocumentsComplete(false);
        return;
      }
      const { completed, total } = getDocumentProgress();
      setDocumentsComplete(total > 0 && completed >= total);
    };
    void fetchDocumentProgress();
    return () => {
      isCurrent = false;
    };
  }, [selectedApp, loadChecklist, getDocumentProgress]);

  const handleGoToDocuments = useCallback(() => {
    if (!selectedApp) return;
    void navigate(`/documents?applicationId=${encodeURIComponent(selectedApp.applicationId)}`);
  }, [navigate, selectedApp]);

  const handleGoToInterview = useCallback(() => {
    if (!selectedApp) return;
    void navigate(`/video?applicationId=${encodeURIComponent(selectedApp.applicationId)}`);
  }, [navigate, selectedApp]);

  const handleSelectApp = useCallback(
    (app: ExistingApplication) => {
      setSelectedApp(app);
      setDocumentsComplete(false);
      setSearchParams({ applicationId: app.applicationId });
    },
    [setSearchParams],
  );

  const handleBackToList = useCallback(() => {
    setSelectedApp(null);
    setSearchParams({});
  }, [setSearchParams]);

  const handleContinueDraft = useCallback(
    (app: ExistingApplication) => {
      if (app.jobPostingId) {
        useApplicationStore
          .getState()
          .setJobPosting(app.jobPostingId, app.jobPostingTitle, app.jobPostingLocation, app.jobPostingSiteState);
      }
      void navigate('/wizard');
    },
    [navigate],
  );

  const stepStatuses = useMemo(
    () => deriveStepStatuses(selectedApp?.status ?? 'submitted', documentsComplete, selectedApp?.decisionContext),
    [selectedApp?.status, documentsComplete, selectedApp?.decisionContext],
  );

  const steps: TimelineStep[] = useMemo(() => {
    if (!selectedApp) return [];

    return [
      {
        id: 1,
        titleKey: 'dashboard.steps.application.title',
        descKey: 'dashboard.steps.application.desc',
        status: stepStatuses.application,
        icon: FileText,
        timestamp: selectedApp.submittedAt,
      },
      {
        id: 2,
        titleKey: 'dashboard.steps.documents.title',
        descKey: 'dashboard.steps.documents.desc',
        status: stepStatuses.documents,
        icon: Upload,
        actionKey:
          stepStatuses.documents !== 'completed' && stepStatuses.documents !== 'pending'
            ? 'dashboard.steps.documents.action'
            : undefined,
        onAction:
          stepStatuses.documents !== 'completed' && stepStatuses.documents !== 'pending'
            ? handleGoToDocuments
            : undefined,
      },
      {
        id: 3,
        titleKey: 'dashboard.steps.interview.title',
        descKey: 'dashboard.steps.interview.desc',
        status: stepStatuses.interview,
        icon: Video,
        actionKey:
          stepStatuses.interview === 'action_required' || stepStatuses.interview === 'pending'
            ? 'dashboard.steps.interview.action'
            : undefined,
        onAction:
          stepStatuses.interview === 'action_required' || stepStatuses.interview === 'pending'
            ? handleGoToInterview
            : undefined,
      },
      {
        id: 4,
        titleKey: 'dashboard.steps.review.title',
        descKey: `dashboard.steps.review.desc.${selectedApp.status === 'approved' || selectedApp.status === 'rejected' || selectedApp.status === 'waitlist' ? selectedApp.status : 'pending'}`,
        status: stepStatuses.review,
        icon: Search,
      },
    ];
  }, [selectedApp, stepStatuses, handleGoToDocuments, handleGoToInterview]);

  if (isLoading) {
    return (
      <div className='flex items-center justify-center min-h-[400px]'>
        <div className='h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent' />
      </div>
    );
  }

  // Empty state — no applications at all
  if (applications.length === 0) {
    return (
      <div className='max-w-3xl mx-auto p-4 md:p-8'>
        <div className='mb-5'>
          <h1 className='text-3xl md:text-4xl font-bold text-gray-900'>{t('dashboard.myApplications')}</h1>
          <p className='text-gray-500 mt-2'>{t('dashboard.myApplicationsSubtitle')}</p>
        </div>
        <div className='bg-white rounded-xl shadow-sm border border-gray-200 p-8 text-center'>
          <FileText className='h-12 w-12 text-gray-300 mx-auto mb-4' />
          <h2 className='text-xl font-semibold text-gray-900 mb-2'>{t('dashboard.noApplications')}</h2>
          <p className='text-gray-500 mb-5'>{t('dashboard.noApplicationsHint')}</p>
          <Link
            to='/jobs'
            className='inline-flex items-center px-8 py-3.5 bg-primary text-white text-lg rounded-lg hover:bg-primary-dark font-bold shadow-md'>
            {t('dashboard.browseJobs')}
          </Link>
        </div>
      </div>
    );
  }

  // Detail view — selected application timeline
  if (selectedApp) {
    const iconStyle = getStatusIconStyle(selectedApp.status);

    return (
      <div className='max-w-3xl mx-auto p-4 md:p-8'>
        {/* Back link */}
        <button
          type='button'
          onClick={handleBackToList}
          className='flex items-center gap-2 text-sm text-gray-500 hover:text-gray-900 mb-5'>
          <ArrowLeft size={16} />
          {t('dashboard.backToList')}
        </button>

        {/* Header */}
        <div className='mb-5'>
          <h1 className='text-3xl md:text-4xl font-bold text-gray-900'>{t('dashboard.title')}</h1>
          <p className='text-gray-500 mt-2 text-sm md:text-base'>
            {t('dashboard.applicationId')}:{' '}
            <span className='font-mono text-gray-900 font-medium text-xs md:text-base'>
              {selectedApp.applicationNumber}
            </span>
          </p>
        </div>

        {/* Contact Summary */}
        {applicant ? (
          <div className='bg-white rounded-xl shadow-sm border border-gray-200 p-4 mb-6'>
            <div className='flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-gray-600'>
              <span className='font-medium text-gray-900'>
                {selectedApp.firstName ?? applicant.email ?? applicant.phone}
                {selectedApp.lastName ? ` ${selectedApp.lastName}` : ''}
              </span>
              {(applicant.email ?? applicant.phone) ? (
                <>
                  <span className='hidden sm:inline text-gray-300'>|</span>
                  <span className='break-all'>{applicant.email ?? applicant.phone}</span>
                </>
              ) : null}
              {selectedApp.submittedAt ? (
                <>
                  <span className='hidden sm:inline text-gray-300'>|</span>
                  <span>
                    {t('dashboard.submitted')} {new Date(selectedApp.submittedAt).toLocaleDateString()}
                  </span>
                </>
              ) : null}
            </div>
          </div>
        ) : null}

        {/* Status Card */}
        <div className='bg-white rounded-xl shadow-sm border border-gray-200 p-4 md:p-6 mb-5'>
          <div className='flex flex-wrap items-center gap-3 md:gap-4 mb-4'>
            <div className={cn('p-2.5 md:p-3 rounded-full shrink-0', iconStyle.bg)}>
              <Clock className={cn('h-5 w-5 md:h-6 md:w-6', iconStyle.text)} />
            </div>
            <div className='flex-1 min-w-0'>
              <h2 className='text-xl md:text-2xl font-bold text-gray-900'>
                {t(getStatusHeaderKey(selectedApp.status))}
              </h2>
              {selectedApp.submittedAt ? (
                <p className='text-gray-500 text-xs md:text-sm'>
                  {t('dashboard.status.submittedAt')}: {new Date(selectedApp.submittedAt).toLocaleDateString()}
                </p>
              ) : null}
            </div>

            {/* Decision badge for final statuses */}
            {selectedApp.status === 'approved' ||
            selectedApp.status === 'rejected' ||
            selectedApp.status === 'waitlist' ? (
              <span
                className={cn(
                  'px-3 py-1 rounded-full text-xs md:text-sm font-semibold shrink-0',
                  selectedApp.status === 'approved' && 'bg-green-100 text-green-700',
                  selectedApp.status === 'rejected' && 'bg-red-100 text-red-700',
                  selectedApp.status === 'waitlist' && 'bg-amber-100 text-amber-700',
                )}>
                {t(`dashboard.decision.${selectedApp.status}`)}
              </span>
            ) : null}
          </div>

          <div className={cn('p-4 rounded-lg text-sm', getMessageBannerStyle(selectedApp.status))}>
            {t(getStatusMessageKey(selectedApp.status))}
          </div>

          {/* Decision context: reason + notes + reupload CTA when applicable */}
          {(selectedApp.status === 'waitlist' || selectedApp.status === 'rejected') && selectedApp.decisionContext ? (
            <div className='mt-3 space-y-2 rounded-lg border border-gray-200 bg-gray-50 p-4 text-sm'>
              {selectedApp.decisionContext.reasonCode ? (
                <div>
                  <span className='font-semibold text-gray-700'>{t('dashboard.decisionDetail.reasonLabel')}:</span>{' '}
                  <span className='text-gray-800'>
                    {t(`dashboard.reasonCodes.${selectedApp.decisionContext.reasonCode}`, {
                      defaultValue: selectedApp.decisionContext.reasonCode,
                    })}
                  </span>
                </div>
              ) : null}
              {selectedApp.decisionContext.notes ? (
                <div>
                  <span className='font-semibold text-gray-700'>{t('dashboard.decisionDetail.notesLabel')}:</span>{' '}
                  <span className='whitespace-pre-wrap break-words text-gray-800'>
                    {selectedApp.decisionContext.notes}
                  </span>
                </div>
              ) : null}
              {selectedApp.status === 'waitlist' &&
              selectedApp.decisionContext.reasonCode &&
              DOC_RELATED_REASON_CODES.has(selectedApp.decisionContext.reasonCode) ? (
                <div className='pt-2'>
                  <button
                    type='button'
                    onClick={handleGoToDocuments}
                    className='inline-flex items-center gap-1.5 rounded-md bg-amber-600 px-4 py-2 text-sm font-semibold text-white hover:bg-amber-700'>
                    <Upload size={14} />
                    {t('dashboard.decisionDetail.reuploadCta')}
                  </button>
                </div>
              ) : null}
            </div>
          ) : null}
        </div>

        {/* Timeline */}
        <div className='mb-8'>
          <h3 className='text-lg font-bold text-gray-900 mb-4'>{t('dashboard.nextSteps')}</h3>
          <div className='relative'>
            {steps.map((step, index) => {
              const Icon = step.icon;
              const isCompleted = step.status === 'completed';
              const isActive = step.status === 'action_required' || step.status === 'in_progress';
              const isPending = step.status === 'pending';
              const isLast = index === steps.length - 1;

              return (
                <div key={step.id} className='relative flex gap-4'>
                  {/* Timeline connector */}
                  <div className='flex flex-col items-center'>
                    <div
                      className={cn(
                        'flex items-center justify-center w-10 h-10 rounded-full shrink-0 z-10',
                        isCompleted && 'bg-green-100',
                        isActive && 'bg-primary/10',
                        isPending && 'bg-gray-100',
                      )}>
                      {isCompleted ? (
                        <CheckCircle size={20} className='text-green-600' />
                      ) : isActive ? (
                        step.status === 'in_progress' ? (
                          <Loader2 size={20} className='text-primary animate-spin' />
                        ) : (
                          <Icon size={20} className='text-primary' />
                        )
                      ) : (
                        <Circle size={20} className='text-gray-400' />
                      )}
                    </div>
                    {!isLast ? (
                      <div className={cn('w-0.5 flex-1 min-h-[24px]', isCompleted ? 'bg-green-300' : 'bg-gray-200')} />
                    ) : null}
                  </div>

                  {/* Step content */}
                  <div className={cn('pb-6 flex-1', isLast && 'pb-0')}>
                    <div
                      className={cn(
                        'bg-white p-4 rounded-lg border',
                        isCompleted && 'border-green-200 bg-green-50/30',
                        isActive && 'border-primary/30 bg-primary/5',
                        isPending && 'border-gray-200',
                      )}>
                      <div className='flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3'>
                        <div className='flex-1 min-w-0'>
                          <div className='flex items-center gap-2'>
                            <h4 className='font-bold text-gray-900'>{t(step.titleKey)}</h4>
                            {isCompleted ? (
                              <span className='text-xs font-medium text-green-600 bg-green-100/50 px-2 py-0.5 rounded-full'>
                                {t('dashboard.done')}
                              </span>
                            ) : null}
                            {step.status === 'in_progress' ? (
                              <span className='text-xs font-medium text-blue-600 bg-blue-100/50 px-2 py-0.5 rounded-full'>
                                {t('dashboard.inProgress')}
                              </span>
                            ) : null}
                          </div>
                          <p className='text-sm text-gray-500 mt-1'>{t(step.descKey)}</p>
                          {isCompleted && step.timestamp ? (
                            <p className='text-xs text-gray-400 mt-1'>
                              {new Date(step.timestamp).toLocaleDateString()}
                            </p>
                          ) : null}
                        </div>
                        {step.onAction && step.actionKey ? (
                          <button
                            type='button'
                            onClick={step.onAction}
                            className='text-base bg-primary text-white px-5 py-2.5 rounded-lg hover:bg-primary-dark font-bold whitespace-nowrap shadow-sm'>
                            {t(step.actionKey)}
                          </button>
                        ) : null}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  }

  // List view — show all application cards
  return (
    <div className='max-w-3xl mx-auto p-4 md:p-8'>
      <div className='mb-5'>
        <h1 className='text-3xl md:text-4xl font-bold text-gray-900'>{t('dashboard.myApplications')}</h1>
        <p className='text-gray-500 mt-2 text-base md:text-lg'>{t('dashboard.myApplicationsSubtitle')}</p>
        <p
          className={cn(
            'text-sm font-medium mt-1',
            applications.filter((a) => a.status !== 'rejected').length >= 5 ? 'text-amber-600' : 'text-gray-400',
          )}>
          {applications.filter((a) => a.status !== 'rejected').length >= 5
            ? t('jobPostings.applicationLimit.counterReached')
            : t('jobPostings.applicationLimit.counter', {
                count: applications.filter((a) => a.status !== 'rejected').length,
              })}
        </p>
      </div>

      <div className='space-y-3'>
        {applications.map((app) => {
          const isDraft = app.status === 'draft';
          const stepNumber = Math.min((app.currentStep ?? 0) + 1, TOTAL_WIZARD_STEPS);
          const progressPct = isDraft ? Math.round((stepNumber / TOTAL_WIZARD_STEPS) * 100) : 0;
          const nextStepText = isDraft
            ? t('dashboard.nextStep.draft', { step: stepNumber, total: TOTAL_WIZARD_STEPS })
            : t(`dashboard.nextStep.${app.status}`, { defaultValue: '' });
          const { icon: NextStepIcon, text: nextStepTextClass } = getNextStepVisual(app.status);

          return (
            <div
              key={app.applicationId}
              className='bg-white rounded-xl shadow-sm border border-gray-200 p-5 transition hover:shadow-md'>
              {/* Header: title + status badge */}
              <div className='flex items-start justify-between gap-4'>
                <div className='flex-1 min-w-0'>
                  <h3 className='text-lg md:text-xl font-bold text-gray-900 leading-tight'>
                    {app.jobPostingTitle
                      ? `${t('dashboard.applicationFor')} ${app.jobPostingTitle}`
                      : t('dashboard.applicationFor')}
                  </h3>
                  {(app.jobPostingRole ?? app.jobPostingLocation) ? (
                    <p className='text-sm text-gray-500 mt-1'>
                      {app.jobPostingRole ? t(`jobPostings.roles.${app.jobPostingRole}`) : ''}
                      {app.jobPostingRole && app.jobPostingLocation ? ' \u00b7 ' : ''}
                      {app.jobPostingLocation ?? ''}
                    </p>
                  ) : null}
                </div>
                <span
                  className={cn(
                    'inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium shrink-0',
                    getStatusBadgeStyle(app.status),
                  )}>
                  <span className={cn('h-1.5 w-1.5 rounded-full', getStatusDotColor(app.status))} />
                  {t(`dashboard.statusLabel.${app.status}`)}
                </span>
              </div>

              {/* Next-step line with icon */}
              {nextStepText ? (
                <div className={cn('flex items-center gap-2 mt-4', nextStepTextClass)}>
                  <NextStepIcon size={18} aria-hidden='true' className='shrink-0' />
                  <span className='text-sm font-medium'>{nextStepText}</span>
                </div>
              ) : null}

              {/* Draft progress bar */}
              {isDraft ? (
                <div
                  className='mt-2 h-1.5 w-full rounded-full bg-gray-100 overflow-hidden'
                  role='progressbar'
                  aria-valuenow={progressPct}
                  aria-valuemin={0}
                  aria-valuemax={100}
                  aria-label={nextStepText}>
                  <div className='h-full bg-primary rounded-full transition-all' style={{ width: `${progressPct}%` }} />
                </div>
              ) : null}

              {/* Meta row: submitted date + last updated + app number */}
              <div className='mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-400'>
                {!isDraft && app.submittedAt ? (
                  <span>{`${t('dashboard.submitted')} ${new Date(app.submittedAt).toLocaleDateString()}`}</span>
                ) : null}
                {app.updatedAt ? <span>{formatRelativeUpdated(app.updatedAt, t)}</span> : null}
                <span className='font-mono'>{app.applicationNumber}</span>
              </div>

              {/* Action button */}
              <div className='mt-4 flex justify-end'>
                {isDraft ? (
                  <button
                    type='button'
                    onClick={() => handleContinueDraft(app)}
                    className='inline-flex items-center gap-2 bg-primary text-white px-5 py-2.5 rounded-lg hover:bg-primary-dark font-bold shadow-sm'>
                    {t('dashboard.continueDraft')}
                  </button>
                ) : (
                  <button
                    type='button'
                    onClick={() => handleSelectApp(app)}
                    className='inline-flex items-center gap-2 bg-white text-gray-700 px-5 py-2.5 rounded-lg border border-gray-300 hover:bg-gray-50 font-medium'>
                    {t('dashboard.viewDetails')}
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
